// "Test query": list the source once with the chosen secret, as the poller will, and show how
// many items match and the first few. Nothing is stored; a result stays on screen only while
// the settings it was made from are unchanged. The source's own refusal (a JQL syntax error,
// an unknown repository) is shown as it came.
import { ArrowSquareOut, CheckCircle, MagnifyingGlass, Tray } from "@phosphor-icons/react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "rizzui";

import { useT } from "../../../i18n";
import { ErrorState } from "../../../ui/states";
import { Mono, Pill } from "../../../ui/status";
import { type Preview, previewSource, type PreviewRequest } from "../api";

const SHOWN = 5;

function Result({ preview }: { preview: Preview }) {
  const t = useT().tasks.create.preview;
  if (preview.items.length === 0) {
    return (
      <div className="mt-3 flex items-start gap-2.5 text-[13px] text-gray-600">
        <Tray weight="duotone" className="h-5 w-5 shrink-0 text-gray-400" aria-hidden />
        <span>
          <span className="block font-medium text-gray-900">{t.none}</span>
          {t.noneBody}
        </span>
      </div>
    );
  }
  return (
    <div className="mt-3">
      <p className="m-0 flex items-center gap-1.5 text-sm font-semibold text-green-dark" role="status">
        <CheckCircle weight="fill" className="h-4 w-4" aria-hidden /> {t.matches(preview.items.length, preview.complete)}
      </p>
      <ul className="m-0 mt-2 list-none divide-y divide-muted/70 rounded-lg border border-muted bg-gray-0 p-0 dark:bg-gray-50" aria-label={t.firstItems}>
        {preview.items.slice(0, SHOWN).map((item) => (
          <li key={item.ref} className="flex items-center gap-3 px-3 py-2">
            <Mono className="shrink-0 text-[12px]">{item.ref}</Mono>
            <span className="min-w-0 flex-1 truncate text-[13px] text-gray-800">{item.title}</span>
            <Pill tone={item.open ? "green" : "gray"}>{item.open ? t.open : t.closed}</Pill>
            {item.url ? (
              <a href={item.url} target="_blank" rel="noreferrer" className="shrink-0 text-gray-500 hover:text-primary" aria-label={t.openItem(item.ref)}>
                <ArrowSquareOut className="h-4 w-4" />
              </a>
            ) : null}
          </li>
        ))}
      </ul>
      {preview.items.length > SHOWN ? <p className="mb-0 mt-1.5 text-xs text-gray-500">{t.more(preview.items.length - SHOWN)}</p> : null}
    </div>
  );
}

export function PreviewPanel({ request, unavailable }: { request: PreviewRequest | null; unavailable: string | null }) {
  const t = useT().tasks.create.preview;
  const test = useMutation({ mutationFn: previewSource });
  const key = JSON.stringify(request);
  const current = test.variables !== undefined && JSON.stringify(test.variables) === key;
  return (
    <section aria-label={t.title} className="rounded-xl border border-muted bg-gray-50/70 p-4 dark:bg-gray-100/40">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-gray-900">{t.title}</h3>
          <p className="mb-0 mt-0.5 text-[13px] text-gray-500">{unavailable ?? t.description}</p>
        </div>
        <Button variant="outline" className="gap-1.5" disabled={request === null} isLoading={test.isPending} onClick={() => request && test.mutate(request)}>
          <MagnifyingGlass className="h-4 w-4" aria-hidden /> {current && !test.isPending ? t.again : t.run}
        </Button>
      </div>
      {current && test.isSuccess ? <Result preview={test.data} /> : null}
      {current && test.isError ? <ErrorState className="mt-3" title={t.failed} message={test.error.message} /> : null}
      {!current && test.data ? <p className="mb-0 mt-2 text-xs text-gray-500">{t.stale}</p> : null}
    </section>
  );
}
